import { useEffect } from "react";
import { supabase } from "./lib/supabase";

const clean=(value:string|null|undefined)=>(value||"").replace(/\s+/g," ").trim();

type WeldRow={id:string;wpds_id:string|null;welder_id:string|null;status:string|null;process:string|null;position:string|null;thickness_mm:number|null};

async function currentProjectId(){
  const head=document.querySelector<HTMLElement>(".project-detail-head");
  if(!head)return null;
  const projectNumber=clean(head.querySelector(".eyebrow")?.textContent);
  const projectName=clean(head.querySelector("h1")?.textContent);
  let query=supabase.from("projects").select("id").limit(1);
  if(projectNumber&&projectNumber!=="PROJECT")query=query.eq("project_number",projectNumber);
  else if(projectName)query=query.eq("name",projectName);
  else return null;
  const {data}=await query.maybeSingle();
  return data?.id as string|undefined;
}

function setIndicator(row:HTMLElement,good:boolean){
  const icon=row.querySelector<HTMLElement>("span");
  if(!icon)return;
  icon.className=good?"check-good":"check-review";
  icon.textContent=good?"✓":"!";
}

export default function ProjectOverviewWeldSync(){
  useEffect(()=>{
    let stopped=false;
    let timer:number|undefined;
    let lastSignature="";

    const apply=async()=>{
      if(stopped)return;
      const overview=document.querySelector<HTMLElement>(".project-overview-main");
      if(!overview)return;
      const projectId=await currentProjectId();
      if(!projectId)return;

      const {data,error}=await supabase
        .from("project_weld_register")
        .select("id,wpds_id,welder_id,status,process,position,thickness_mm")
        .eq("project_id",projectId);
      if(error)return;

      const welds=(data||[]) as WeldRow[];
      const total=welds.length;
      const ready=welds.filter(w=>{
        const status=clean(w.status).toLowerCase();
        return ["ready","in_progress","accepted"].includes(status);
      }).length;
      const assigned=welds.filter(w=>!!w.welder_id).length;
      const signature=`${projectId}:${total}:${ready}:${assigned}:${welds.map(w=>`${w.id}:${w.status}:${w.welder_id||""}`).join("|")}`;
      if(signature===lastSignature)return;
      lastSignature=signature;

      const rows=[...overview.querySelectorAll<HTMLElement>(".readiness-list > div")];
      const weldRow=rows.find(row=>/weld/i.test(clean(row.querySelector("b")?.textContent)));
      const assignmentRow=rows.find(row=>/assignment/i.test(clean(row.querySelector("b")?.textContent))||/eligibility snapshots/i.test(clean(row.querySelector("small")?.textContent)));

      if(weldRow){
        const value=weldRow.querySelector<HTMLElement>("b");
        const note=weldRow.querySelector<HTMLElement>("small");
        if(value)value.textContent=`${ready} of ${total} welds ready`;
        if(note)note.textContent="WPDS and production details confirmed in Weld Register";
        setIndicator(weldRow,total>0&&ready===total);
      }

      if(assignmentRow){
        const value=assignmentRow.querySelector<HTMLElement>("b");
        const note=assignmentRow.querySelector<HTMLElement>("small");
        if(value)value.textContent=`${assigned} of ${total} welds assigned`;
        if(note)note.textContent="Qualified welders assigned in Weld Register";
        setIndicator(assignmentRow,total>0&&assigned===total);
      }
    };

    const schedule=()=>{
      window.clearTimeout(timer);
      timer=window.setTimeout(()=>void apply(),120);
    };

    const observer=new MutationObserver(schedule);
    observer.observe(document.body,{childList:true,subtree:true});
    document.addEventListener("change",schedule,true);
    window.addEventListener("focus",schedule);
    schedule();

    return()=>{
      stopped=true;
      window.clearTimeout(timer);
      observer.disconnect();
      document.removeEventListener("change",schedule,true);
      window.removeEventListener("focus",schedule);
    };
  },[]);
  return null;
}
